// "From the album" on Today: a new montage when one's just been made, or a
// photo from a week or a month ago — with how much he's grown since.

import { Images, LayoutGrid } from 'lucide-react';
import { useMemo } from 'react';
import { useCollection, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { photoSrc } from '../../data/photos.ts';
import { DAY, formatDate } from '../../domain/time.ts';
import { albumMemory } from '../../domain/montage.ts';
import type { Photo } from '../../domain/types.ts';
import { navigate } from '../../app/router.ts';
import { Card, CardHeader } from '../../ui/primitives.tsx';

export function AlbumCard() {
  const photos = useCollection('photos');
  const montages = useCollection('montages');
  const profile = useProfile();
  const s = useLiveStatus(60 * 60_000);
  const now = s.now;
  const fresh = useMemo(() => montages.filter((m) => m.auto && now - m.at < 3 * DAY).sort((a, b) => b.at - a.at)[0], [montages, now]);
  const memory = useMemo(() => albumMemory(photos, now), [photos, now]);
  const byId = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);

  if (fresh) {
    const pics = fresh.photos.map((id) => byId.get(id)).filter((p): p is Photo => !!p).slice(0, 4);
    return (
      <Card className="album-card" onClick={() => navigate(`/diary/montage/${fresh.id}`)}>
        <CardHeader title="New montage ready" icon={LayoutGrid} sub={`${fresh.title} · ${fresh.photos.length} photos`} />
        <span className={`montage-preview album-montage n${pics.length}`}>
          {pics.map((p) => (
            <img key={p.id} src={p.data} alt="" />
          ))}
        </span>
        <span className="link small">Open, share or save it</span>
      </Card>
    );
  }
  if (!memory) return null;
  const { photo, label } = memory;
  const then = photo.ageWeeks;
  const nowWeeks = s.age?.weeks;
  const grown = then !== undefined && nowWeeks !== undefined && nowWeeks > then ? `He was ${then} weeks — now ${nowWeeks}.` : formatDate(photo.at, 'long');
  return (
    <Card className="album-card" onClick={() => navigate('/diary')}>
      <CardHeader title={`${label} today`} icon={Images} sub={grown} />
      <figure className="album-photo">
        <img src={photoSrc(photo)} alt={photo.caption ?? `${profile.name} ${label.toLowerCase()}`} loading="lazy" onError={(e) => ((e.currentTarget.src = photo.data), undefined)} />
        {photo.caption && <figcaption>{photo.caption}</figcaption>}
      </figure>
    </Card>
  );
}
